import { Link } from "react-router-dom";
import { CategoryTag } from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { getArticleById } from "@/mocks/articles";

const deepDiveIds = ["a7", "a5", "a9"];

export default function DeepDiveSection() {
  const lead = getArticleById(deepDiveIds[0]);
  const rest = deepDiveIds.slice(1).map((id) => getArticleById(id)).filter(Boolean);

  if (!lead) return null;

  return (
    <section id="deepdive" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <SectionHeading
          kicker="DEEP DIVE"
          title="深扒专题"
          desc="把一件事拆成时间线、证据链与各方表述，一次讲透，而不是切成一堆碎片热点。"
          action={
            <span className="flex items-center gap-2 whitespace-nowrap text-xs text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-stack-line"></i>
              </span>
              每周更新 2-3 篇
            </span>
          }
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-7">
            <Link to={`/article/${lead.id}`} className="relative block h-[240px] cursor-pointer overflow-hidden md:h-[320px]">
              <img
                src={lead.cover}
                alt={`${lead.title} 深扒专题配图`}
                title={`${lead.title} - 黑料八卦起底`}
                className="h-full w-full object-cover object-top transition-transform duration-700 hover:scale-105"
              />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                <i className="ri-bookmark-3-line"></i>
                专题 01
              </span>
            </Link>
            <div className="p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2">
                <CategoryTag slug={lead.categorySlug} name={lead.category} />
                <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-links-line"></i>
                  </span>
                  证据链 {lead.evidence.length} 条
                </span>
                <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-timer-line"></i>
                  </span>
                  约 {lead.readingMinutes} 分钟读完
                </span>
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                <Link to={`/article/${lead.id}`} className="cursor-pointer transition-colors hover:text-primary-600">
                  {lead.title}
                </Link>
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{lead.excerpt}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {lead.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-50 px-2.5 py-1 text-[11px] text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/article/${lead.id}`}
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读完整深扒
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          <div className="flex flex-col gap-5 lg:col-span-5">
            {rest.map((item, index) =>
              item ? (
                <article
                  key={item.id}
                  className="flex flex-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                >
                  <Link
                    to={`/article/${item.id}`}
                    className="relative h-[92px] w-[120px] flex-none cursor-pointer overflow-hidden rounded-md"
                  >
                    <img
                      src={item.cover}
                      alt={`${item.title} 深扒专题配图`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105"
                    />
                  </Link>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-[11px] font-medium tracking-widest text-accent-700">
                      专题 0{index + 2}
                    </span>
                    <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950">
                      <Link to={`/article/${item.id}`} className="cursor-pointer transition-colors hover:text-primary-600">
                        {item.title}
                      </Link>
                    </h3>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                      {item.excerpt}
                    </p>
                    <div className="mt-auto flex items-center gap-3 pt-3 text-[11px] text-foreground-500">
                      <span>{item.category}</span>
                      <span className="flex items-center gap-1">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-links-line"></i>
                        </span>
                        证据链 {item.evidence.length} 条
                      </span>
                    </div>
                  </div>
                </article>
              ) : null
            )}

            <div className="rounded-lg bg-secondary-950 p-5 md:p-6">
              <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-accent-400">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-quill-pen-line"></i>
                </span>
                编辑部说明
              </p>
              <p className="mt-3 text-sm leading-relaxed text-background-300">
                深扒专题的成稿标准比快讯更严格：至少需要三条可交叉验证的线索，并且时间线必须能自洽。达不到这个标准，我们宁可推迟发布。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-accent-400 transition-colors hover:text-accent-300"
              >
                了解我们的内容原则
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}